import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { HttpClient } from '@angular/common/http';
import { Button } from '../../Components/button/button';
import { SpinnerOverlayService } from '../../Components/spinner/spinner-overlay.service';
import { OrderService } from '../../orders/order-service';
import { environment } from '../../../../environment';

@Component({
  selector: 'app-sales-report',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    Button
  ],
  templateUrl: './sales-report.html',
  styleUrl: '../sales/sales.css'
})
export class SalesReport implements OnInit {
  filters = {
    asesor: '',
    cliente: '',
    fechaDesde: '',
    fechaHasta: '',
    tipoMaterial: ''
  };

  selectedValues = {
    clienteAN: null as number | null,
    asesorAN: null as number | null
  };

  allData = {
    clientes: [] as any[],
    asesores: [] as any[]
  };

  suggestions = {
    cliente: [] as any[],
    asesor: [] as any[]
  };

  showDropdown = {
    cliente: false,
    asesor: false
  };

  flowFromAsesor = false;
  flowFromCliente = false;

  reportSummary: {
    invoicedOrdersCount?: number;
    invoicedOrdersAmount?: number;
    proformaOrdersCount?: number;
    proformaOrdersAmount?: number;
    totalCubicMeters?: number;
    confirmedTotalsByUom?: Record<string, number>;
    draftTotalsByUom?: Record<string, number>;
  } | null = null;

  confirmedTotalsList: { uom: string; value: number }[] = [];
  draftTotalsList: { uom: string; value: number }[] = [];
  showProformaSummary = false;
  showDraftTotals = false;

  constructor(
    private orderService: OrderService,
    private spinner: SpinnerOverlayService,
    private http: HttpClient
  ) {}

  ngOnInit() {
    this.findAll();
    this.loadInitialFiltersData();
  }

  loadInitialFiltersData() {
    const baseUrl = environment.wsApiBaseUrl;

    this.http.get<any>(`${baseUrl}/api/customer?nameAccountManager=`).subscribe({
      next: (res) => this.allData.asesores = res.accountManagers || []
    });

    this.http.get<any>(`${baseUrl}/api/customer?name=`).subscribe({
      next: (res) => this.allData.clientes = res.customer || res.customers || []
    });

  }

  onFocus(type: 'cliente' | 'asesor') {
    this.showDropdown[type] = true;
    this.filterLocalData(type);
  }

  onBlur(type: 'cliente' | 'asesor') {
    setTimeout(() => {
      this.showDropdown[type] = false;
    }, 200);
  }

  onSearchInput(type: 'cliente' | 'asesor') {
    const term = this.filters[type];

    if (!term) {
      if (type === 'asesor') {
        this.selectedValues.asesorAN = null;
        this.flowFromAsesor = false;
        this.loadAllClientes();
      }

      if (type === 'cliente') {
        this.selectedValues.clienteAN = null;
        this.flowFromCliente = false;
      }

    }

    this.showDropdown[type] = true;
    this.filterLocalData(type);
  }

  filterLocalData(type: 'cliente' | 'asesor') {
    const term = this.filters[type]?.toLowerCase() || '';

    if (type === 'cliente') {
      this.suggestions.cliente = this.allData.clientes.filter(item =>
        item.name?.toLowerCase().includes(term) || item.ruc?.includes(term)
      );
    } else if (type === 'asesor') {
      this.suggestions.asesor = this.allData.asesores.filter(item =>
        item.name?.toLowerCase().includes(term)
      );
    }
  }

  selectSuggestion(type: 'cliente' | 'asesor', item: any) {
    const baseUrl = environment.wsApiBaseUrl;
    this.filters[type] = item.name;
    this.showDropdown[type] = false;

    if (type === 'asesor') {
      this.flowFromAsesor = true;
      this.selectedValues.asesorAN = item.an;
      this.filters.cliente = '';
      this.selectedValues.clienteAN = null;

      this.http.get<any>(`${baseUrl}/api/customer?anAccountManager=${item.an}`)
        .subscribe(res => {
          this.allData.clientes = res.customers || res.customer || [];
          if (this.allData.clientes.length === 0) {
            this.selectedValues.clienteAN = null;
          }
          this.findAll();
        });
    }

    if (type === 'cliente') {
      this.selectedValues.clienteAN = item.an;
      if (!this.selectedValues.asesorAN) {
        this.flowFromCliente = true;
      }
      this.findAll();
    }
  }

  loadAllClientes() {
    this.http.get<any>(`${environment.wsApiBaseUrl}/api/customer?name=`).subscribe(res => this.allData.clientes = res.customer || res.customers || []);
  }

  findAll() {

    this.spinner.show();

    const params = this.buildParams();

    this.orderService.getOrdersReport(params).subscribe({
      next: (response: any) => {
        const report = response.body?.ordersReport || null;
        this.reportSummary = report;
        this.confirmedTotalsList = this.toTotalsList(report?.confirmedTotalsByUom);
        this.draftTotalsList = this.toTotalsList(report?.draftTotalsByUom);
        this.spinner.hide();
      },
      error: (err: any) => {
        console.error(err);
        this.reportSummary = null;
        this.confirmedTotalsList = [];
        this.draftTotalsList = [];
        this.spinner.hide();
      }
    });
  }

  cleanFilter() {
    this.flowFromAsesor = false;
    this.flowFromCliente = false;

    this.filters = {
      asesor: '',
      cliente: '',
      fechaDesde: '',
      fechaHasta: '',
      tipoMaterial: ''
    };

    this.selectedValues = {
      clienteAN: null,
      asesorAN: null
    };

    this.loadInitialFiltersData();
    this.findAll();
  }

  private buildParams() {
    const params: any = {
      sort: 'createdDate,desc'
    };

    if (this.selectedValues.clienteAN) params.customerAN = this.selectedValues.clienteAN;
    if (this.selectedValues.asesorAN) params.anAccountManager = this.selectedValues.asesorAN;

    if (this.filters.fechaDesde) params.dateFrom = this.filters.fechaDesde;
    if (this.filters.fechaHasta) params.dateTo = this.filters.fechaHasta;

    if (this.filters.tipoMaterial) params.categoryProduct = this.filters.tipoMaterial;

    return params;
  }

  private toTotalsList(totals?: Record<string, number>) {
    if (!totals) {
      return [];
    }
    return Object.entries(totals).map(([uom, value]) => ({ uom, value }));
  }
}
